import { useEffect, useId, useState } from "react";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import {
  buildCalendarMonthGrid,
  calendarWeekdayLabels,
  canShowPreviousCalendarMonth,
  formatArrivalDayLong,
  formatCalendarMonthTitle,
  initialCalendarMonth,
  shiftCalendarMonth,
  stayRangeRole,
  type CalendarMonthView,
} from "@/lib/dates";
import { DURATION, EASE_OUT_EXPO, usePrefersReducedMotion } from "@/lib/motion";

export interface BookingDateCalendarProps {
  value: string;
  min?: string;
  max?: string;
  isDateDisabled?: (iso: string) => boolean;
  /** Anreise – markiert Start des Aufenthalts und Tage dazwischen bis Abreise. */
  rangeStart?: string;
  /** Abreise – markiert Ende des Aufenthalts (inkl. Vorschau beim Auswählen). */
  rangeEnd?: string;
  onChange: (iso: string) => void;
  labelledBy?: string;
}

export function BookingDateCalendar({
  value,
  min,
  max,
  isDateDisabled,
  rangeStart,
  rangeEnd,
  onChange,
  labelledBy,
}: BookingDateCalendarProps) {
  const gridId = useId();
  const reducedMotion = usePrefersReducedMotion();
  const [month, setMonth] = useState<CalendarMonthView>(() => initialCalendarMonth(value, min));
  const [monthDirection, setMonthDirection] = useState<1 | -1>(1);

  useEffect(() => {
    setMonth(initialCalendarMonth(value, min));
  }, [value, min]);

  const cells = buildCalendarMonthGrid(month, min, max, isDateDisabled);
  const canGoPrev = canShowPreviousCalendarMonth(month, min);
  const monthKey = `${month.year}-${month.month}`;

  function shiftMonth(delta: -1 | 1) {
    setMonthDirection(delta);
    setMonth((m) => shiftCalendarMonth(m, delta));
  }

  const gridVariants = reducedMotion
    ? undefined
    : {
        enter: (direction: number) => ({
          opacity: 0,
          x: direction > 0 ? 36 : -36,
        }),
        center: { opacity: 1, x: 0 },
        exit: (direction: number) => ({
          opacity: 0,
          x: direction > 0 ? -36 : 36,
        }),
      };

  return (
    <div
      className="bk-date-calendar"
      role="application"
      aria-labelledby={labelledBy}
      onClick={(e) => e.stopPropagation()}
    >
      {rangeStart ? (
        <p className="bk-date-calendar__anchor-hint">
          Anreise:{" "}
          <span className="font-medium text-slate-800">{formatArrivalDayLong(rangeStart)}</span>
          {rangeEnd && rangeEnd > rangeStart ? (
            <>
              {" "}
              · Abreise:{" "}
              <span className="font-medium text-slate-800">{formatArrivalDayLong(rangeEnd)}</span>
            </>
          ) : null}
        </p>
      ) : null}
      <div className="bk-date-calendar__nav">
        <button
          type="button"
          className="bk-date-calendar__nav-btn"
          aria-label="Vorheriger Monat"
          disabled={!canGoPrev}
          onClick={() => shiftMonth(-1)}
        >
          ‹
        </button>
        <AnimatePresence mode="wait" custom={monthDirection}>
          <motion.p
            key={monthKey}
            className="bk-date-calendar__title"
            aria-live="polite"
            custom={monthDirection}
            variants={gridVariants}
            initial={reducedMotion ? false : "enter"}
            animate="center"
            exit="exit"
            transition={{ duration: DURATION.fast, ease: EASE_OUT_EXPO }}
          >
            {formatCalendarMonthTitle(month)}
          </motion.p>
        </AnimatePresence>
        <button
          type="button"
          className="bk-date-calendar__nav-btn"
          aria-label="Nächster Monat"
          onClick={() => shiftMonth(1)}
        >
          ›
        </button>
      </div>

      <div className="bk-date-calendar__weekdays" aria-hidden="true">
        {calendarWeekdayLabels().map((label) => (
          <span key={label} className="bk-date-calendar__weekday">
            {label}
          </span>
        ))}
      </div>

      <div className="overflow-hidden">
        <AnimatePresence mode="wait" custom={monthDirection}>
          <motion.div
            key={monthKey}
            id={gridId}
            className="bk-date-calendar__grid"
            role="grid"
            custom={monthDirection}
            variants={gridVariants}
            initial={reducedMotion ? false : "enter"}
            animate="center"
            exit="exit"
            transition={{ duration: DURATION.normal, ease: EASE_OUT_EXPO }}
          >
            {cells.map((cell) => {
              const selected = value === cell.iso;
              const rangeRole = stayRangeRole(cell.iso, rangeStart, rangeEnd);
              const inStayRange = rangeRole !== null;
              return (
                <button
                  key={cell.iso}
                  type="button"
                  role="gridcell"
                  aria-selected={selected}
                  aria-disabled={cell.disabled}
                  disabled={cell.disabled}
                  className={clsx(
                    "bk-date-calendar__day",
                    !cell.inMonth && "bk-date-calendar__day--outside",
                    cell.disabled && "bk-date-calendar__day--disabled",
                    inStayRange && "bk-date-calendar__day--in-range",
                    rangeRole === "start" && "bk-date-calendar__day--range-start",
                    rangeRole === "middle" && "bk-date-calendar__day--range-middle",
                    rangeRole === "end" && "bk-date-calendar__day--range-end",
                    selected && "bk-date-calendar__day--selected",
                  )}
                  onClick={() => onChange(cell.iso)}
                >
                  {cell.day}
                </button>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
